import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';
import { UserPermissions } from '../types';

interface User {
  name: string;
  email: string;
  picture: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  permissions: UserPermissions | null;
  login: (credential: string) => void;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SESSION_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);

  const fetchUserPermissions = async (email: string) => {
    try {
      const response = await api.get(`/user-permissions?email=${email}`);
      setPermissions(response.data);
    } catch (error: any) {
      console.error('Fetch permissions error:', error.message);
      setPermissions(null);
    }
  };

  const validateSession = async (userData: User, token: string) => {
    try {
      // Try to validate with user-permissions endpoint (works with both session tokens and Google tokens)
      await api.get('/user-permissions', {
        params: { email: userData.email },
        headers: { Authorization: `Bearer ${token}` }
      });
      return true;
    } catch (error: any) {
      console.error('Session validation error:', error.message);
      return false;
    }
  };

  const refreshToken = async (): Promise<boolean> => {
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      const sessionExpiration = localStorage.getItem('sessionExpiration');

      if (!sessionToken || !sessionExpiration) {
        console.log('No session data to refresh');
        return false;
      }

      const expirationTime = parseInt(sessionExpiration, 10);
      const timeUntilExpiry = expirationTime - Date.now();

      // If session expires within 1 hour, validate and extend it
      if (timeUntilExpiry < 60 * 60 * 1000) {
        console.log('Session expiring soon, validating and extending');

        const isValid = await validateSession(user!, sessionToken);
        if (isValid) {
          // Extend session by another 7 days from now
          const newExpirationTime = Date.now() + SESSION_DURATION;
          localStorage.setItem('sessionExpiration', newExpirationTime.toString());
          console.log('Session extended successfully until:', new Date(newExpirationTime).toISOString());
          return true;
        } else {
          console.log('Session validation failed during refresh');
          return false;
        }
      }

      console.log('Session still valid, no refresh needed');
      return true;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  };

  useEffect(() => {
    const restoreSession = async () => {
      try {
        const storedUser = localStorage.getItem('user');
        const sessionToken = localStorage.getItem('sessionToken');
        const sessionExpiration = localStorage.getItem('sessionExpiration');

        console.log('Restoring session:', {
          storedUser: storedUser ? 'present' : 'missing',
          sessionToken: sessionToken ? 'present' : 'missing',
          sessionExpiration: sessionExpiration ? 'present' : 'missing',
        });

        if (storedUser && sessionToken && sessionExpiration) {
          const expirationTime = parseInt(sessionExpiration, 10);
          if (Date.now() < expirationTime) {
            const userData = JSON.parse(storedUser);
            const isValid = await validateSession(userData, sessionToken);
            if (isValid) {
              console.log('Session valid, setting user');
              setUser(userData);
              await fetchUserPermissions(userData.email);
            } else {
              console.log('Invalid session, attempting refresh');
              const refreshSuccess = await refreshToken();
              if (refreshSuccess) {
                setUser(userData);
                await fetchUserPermissions(userData.email);
              } else {
                console.log('Session refresh failed, clearing');
                logout();
              }
            }
          } else {
            console.log('Session expired, clearing');
            logout();
          }
        } else {
          console.log('Incomplete session data, no user restored');
        }
      } catch (error) {
        console.error('Error accessing localStorage:', error);
        logout();
      } finally {
        setIsLoading(false);
      }
    };

    restoreSession();
  }, []);

  const login = async (credential: string) => {
    try {
      console.log('Attempting login with Google credential');

      // Verify with backend and get session token
      const response = await api.post('/authorize', { credential });
      const { sessionToken, expires, user: userInfo } = response.data;

      const userData: User = {
        name: userInfo.name,
        email: userInfo.email,
        picture: userInfo.picture,
      };

      console.log('Login successful, setting user');
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));

      // Use session token if available, otherwise fall back to Google credential
      if (sessionToken && expires) {
        localStorage.setItem('sessionToken', sessionToken);
        localStorage.setItem('sessionExpiration', expires.toString());
        console.log('Using session token auth');
      } else {
        localStorage.setItem('sessionToken', credential);
        localStorage.setItem('sessionExpiration', (Date.now() + SESSION_DURATION).toString());
        console.log('Using Google credential fallback');
      }

      setPermissions(userInfo); // Set permissions directly from response
    } catch (error: any) {
      console.error('Login error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      alert('Login failed: ' + (error.response?.data?.error || 'Please try again.'));
    }
  };

  const logout = () => {
    console.log('Logging out user');
    setUser(null);
    setPermissions(null);
    localStorage.removeItem('user');
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('sessionExpiration');
  };

  // Auto-refresh token on navigation
  useEffect(() => {
    if (!user) return;

    const handleNavigation = async () => {
      console.log('Navigation detected, checking token refresh');
      await refreshToken();
    };

    // Listen for navigation events (popstate for browser back/forward, hashchange for hash routing)
    window.addEventListener('popstate', handleNavigation);
    window.addEventListener('hashchange', handleNavigation);

    // Also check periodically (every 5 minutes)
    const interval = setInterval(async () => {
      if (user) {
        console.log('Periodic token check');
        await refreshToken();
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => {
      window.removeEventListener('popstate', handleNavigation);
      window.removeEventListener('hashchange', handleNavigation);
      clearInterval(interval);
    };
  }, [user]);

  return (
    <AuthContext.Provider
      value={{ user, isLoading, permissions, login, logout, refreshToken }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};